/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ComponentType, ReactElement, ReactNode } from 'react';
import styled from 'styled-components';

import type { TagPresetType } from '@n3/css-base';

import type {
  TagComponent as TagComponentType,
  TagSize,
  TagVariant,
} from './types';

export type TagProps = {
  /**
   * Цвет тега из стандартной палитры
   */
  preset?: TagPresetType;
  /**
   * Переопределённый цвет текста (только в случае, есле цветов из `preset` недостаточно)
   */
  textColor?: string;
  /**
   * Переопределённый цвет фона (только в случае, есле цветов из `preset` недостаточно)
   */
  backgrondColor?: string;
  /**
   * Переопределённый цвет границы (только в случае, есле цветов из `preset` недостаточно)
   */
  borderColor?: string;

  /**
   * Компонент тега
   */
  component?: TagComponentType;
  /**
   * Дополнительный класс тега
   */
  className?: string;
  /**
   * Стиль отображения тега
   */
  variant?: TagVariant;
  /**
   * Размер (работает только в стандартном стиле)
   */
  size?: TagSize;
  /**
   * Содержимое тега
   */
  children?: ReactNode;

  [key: string]: unknown;
};

export const StyledWrapper = styled.span<{
  $backgrondColor?: string;
  $borderColor?: string;
  $preset: TagPresetType;
  $size: TagSize;
  $textColor?: string;
  $variant: TagVariant;
}>(
  ({
    $backgrondColor,
    $borderColor,
    $preset,
    $size,
    $textColor,
    $variant,

    theme: { colors, fonts, tag },
  }) => {
    const { bg, text, border } = tag.PRESETS[$preset];

    const backgroundColor = $backgrondColor || bg;
    const borderColor = $borderColor || border;

    if ($variant === 'inline') {
      return {
        ...fonts[tag.FONT_INLINE],
        display: 'inline-block',
        position: 'relative',
        padding: 0,
        margin: 0,
        border: 'none',
        outline: 'none',
        textDecoration: 'none',
        color: colors.NEUTRAL_DEFAULT,

        '&, &:hover': {
          backgroundColor: 'transparent',
          color: colors.NEUTRAL_DEFAULT,
        },

        '&::before': {
          content: '""',
          display: 'block',
          position: 'absolute',
          top: '50%',
          marginTop: -tag.SMALL_DOT_WIDTH / 2,
          left: 0,
          width: tag.SMALL_DOT_WIDTH,
          height: tag.SMALL_DOT_WIDTH,
          borderRadius: '50%',
          backgroundColor,
        },
      };
    }

    return {
      ...fonts[$size === 'small' ? tag.FONT_SMALL : tag.FONT_DEFAULT],
      boxSizing: 'border-box',
      display: 'inline-flex',
      alignItems: 'center',
      position: 'relative',
      margin: 0,
      outline: 'none',
      borderRadius: tag.BORDER_RADIUS,
      textDecoration: 'none',
      paddingLeft: tag.SIDE_PADDING,
      paddingRight: tag.SIDE_PADDING,
      minHeight: $size === 'small' ? tag.HEIGHT_SMALL : tag.HEIGHT,
      borderWidth: tag.BORDER_WIDTH,
      borderStyle: 'solid',
      borderColor: borderColor || backgroundColor,

      '&, &:hover': {
        backgroundColor,
        color: $textColor || text,
      },
    };
  },
);

export const StyledContent = styled.div<{
  $variant: TagVariant;
}>(
  ({
    $variant,

    theme: { tag },
  }) => {
    if ($variant === 'inline') {
      return {
        paddingLeft: tag.SMALL_DOT_WIDTH + tag.SMALL_DOT_OFFSET,
      };
    }

    return {};
  },
);

export function Tag({
  component: TagComponent = 'span',
  className = '',
  variant = 'default',
  size = 'medium',
  preset = 'light_gray',
  textColor = undefined,
  backgrondColor = undefined,
  borderColor = undefined,

  children = undefined,

  ...rest
}: TagProps): ReactElement {
  return (
    <StyledWrapper
      $preset={preset}
      $size={size}
      $variant={variant}
      $textColor={textColor}
      $backgrondColor={backgrondColor}
      $borderColor={borderColor}
      as={TagComponent as ComponentType<any>}
      className={className}
      {...rest}
    >
      <StyledContent $variant={variant}>{children}</StyledContent>
    </StyledWrapper>
  );
}
